import React, { useEffect } from 'react'
import {
  Box,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import FemaleIcon from '@mui/icons-material/Female'
import MaleIcon from '@mui/icons-material/Male'
import StarIcon from '@mui/icons-material/Star'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import {
  Entity,
  EventResponse,
  EventResultObject,
  FetchResponse,
  MemberResponse,
  Method,
  StatisticResponse,
} from '../../../shared/types'
import { useDispatch, useSelector } from 'react-redux'
import { getEventResults, getEvents, getMembers, getStatistics } from '../../../store/data/data.selectors'
import BarChart from '../../../shared/components/ChartComponents/BarChart'
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js'
import GoodIcon from '../../../shared/assets/icons/GoodIcon'
import BadIcon from '../../../shared/assets/icons/BadIcon'
import BestNewcomerIcon from '../../../shared/assets/icons/BestNewcomerIcon'
import OldestGamerIcon from '../../../shared/assets/icons/OldestGamerIcon'
import YoungestGamerIcon from '../../../shared/assets/icons/YoungestGamerIcon'
import WorstTournamentIcon from '../../../shared/assets/icons/WorstTournamentIcon'
import BestTournamentIcon from '../../../shared/assets/icons/BestTournamentIcon'
import YoungestWinnerIcon from '../../../shared/assets/icons/YoungestWinnerIcon'
import OldestWinnerIcon from '../../../shared/assets/icons/OldestWinnerIcon'
import StarsIcon from '../../../shared/assets/icons/StarsIcon'
import DropDownComponent, { DropDownElement } from '../../../shared/components/DropDownComponent'
import PieChart from '../../../shared/components/ChartComponents/PieChart'
import CardIcon from '../../../shared/assets/icons/CardIcon'
import RoundIcon from '../../../shared/assets/icons/RoundIcon'
import getSortedResults from '../../../util/getSortedResults'
import { getAppTheme } from '../../../shared/config/getAppTheme'
import { makeStyles } from '@mui/styles'
import { SortingOption } from '../Events/EventResults/EventResults'
import { getApiVersion } from '../../../shared/config/getApiVersion'
import * as dataActions from '../../../store/data/data.actions'
import fetchData from '../../../fetch/fetchData'
import Preloader from '../../../shared/components/Preloader'
import HandIcon from '../../../shared/assets/icons/HandIcon'
import BadSmileyIcon from '../../../shared/assets/icons/BadSmileyIcon'
import CardsIcon from '../../../shared/assets/icons/CardsIcon'
import SecondPlaceIcon from '../../../shared/assets/icons/SecondPlaceIcon'
import PodiumIcon from '../../../shared/assets/icons/PodiumIcon'
import BetterSecondSessionIcon from '../../../shared/assets/icons/BetterSecondSessionIcon'
import BetterTournamentIcon from '../../../shared/assets/icons/BetterTournamentIcon'
import MostFollowingWinsIcon from '../../../shared/assets/icons/MostFollowingWinsIcon'
import ScaleIcon from '../../../shared/assets/icons/ScaleIcon'
import RocketIcon from '../../../shared/assets/icons/RocketIcon'
import PerfectIcon from '../../../shared/assets/icons/PerfectIcon'
import BestRoundResultIcon from '../../../shared/assets/icons/BestRoundResultIcon'
import Env from '../../../shared/config/envConfig'
import { addCsrfHeaders } from '../../../shared/config/csrf'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { LogarithmicScale } from 'chart.js/auto'
import AverageIcon from '../../../shared/assets/icons/AverageIcon'
import BetterFirstSessionIcon from '../../../shared/assets/icons/BetterFirstSessionIcon'

interface Stars {
  memberId: string
  count: number
}

const useStyles = makeStyles((theme: Theme) => ({
  recordName: {
    marginLeft: '10px',
    width: '170px',
  },
  icon: {
    width: '20px',
  },
  recordElement: {},
  cell: {
    padding: '8px',
  },
}))

const Statistics: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const statisticColors = ['#1c7899', '#6093a4', '#8baab6', '#a9c3ce', '#a0a8ad', '#4e5a60', '#d0e0e5', '#6ba5a1', '#517e8c', '#aac7b3', '#6c7a80', '#2d3e49']

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const theme: Theme = getAppTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const members: MemberResponse | undefined = useSelector(getMembers)
  const statistics: StatisticResponse | undefined = useSelector(getStatistics)
  const eventResults: EventResultObject | undefined = useSelector(getEventResults)
  const events: EventResponse | undefined = useSelector(getEvents)

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    LogarithmicScale,
  )

  const pieOptions = {
    responsive: true,
    borderColor: theme.palette.background.paper,
    plugins: {
      legend: {
        position: 'top',
        display: true,
      },
      datalabels: {
        color: theme.palette.text.primary,
        formatter: (value, context) => {
          const sum = context.chart.data.datasets[0].data.reduce((s, val) => s + val, 0)
          const percentage = Math.round((value / sum) * 100)
          return percentage > 5 ? `${value} (${percentage}%)` : '' // Nur Labels anzeigen, wenn der Prozentsatz > 5% ist
        },
        font: {
          weight: 'bold',
          size: 12,
        },
        display: function (context) {
          return context.dataset.data[context.dataIndex] > 0 // Zeige nur Werte > 0
        },
        textAlign: 'center',
        anchor: 'center',
        align: 'center',
        offset: 0,
      },
    },
  }

  const stackedOptions = {
    responsive: true,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        display: true,
      },
      datalabels: {
        color: theme.palette.text.primary,
        anchor: 'end',
        align: 'bottom',
        offset: 4,
        clamp: true,
        formatter: (value) => value || '',
        font: {
          weight: 'bold',
          size: 12,
        },
        display: function (context) {
          return context.dataset.data[context.dataIndex] > 0
        },
      },
    },
  }

  const nonStackedOptions = {
    responsive: true,
    scales: {
      y: {
        type: 'logarithmic',
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return Number(value).toLocaleString() // Formatierung
          },
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        display: false,
      },
      datalabels: {
        color: theme.palette.text.primary,
        anchor: 'end',
        align: 'bottom',
        offset: 4,
        clamp: true,
        formatter: (value, context) => {
          // Für Pie/Doughnut-Charts ist die Berechnung einfacher
          const dataset = context.chart.data.datasets[context.datasetIndex]
          const total = dataset.data.reduce((sum, val) => sum + (val || 0), 0)
          const percentage = total ? ((value / total) * 100).toFixed(1) : '0'

          // Entferne die Dezimalstelle, wenn sie .0 ist
          const displayPercentage = percentage.endsWith('.0')
            ? percentage.slice(0, -2)
            : percentage

          // Gib sowohl absoluten Wert als auch Prozentsatz zurück
          return value ? `${value} (${displayPercentage}%)` : ''
        },
        font: {
          weight: 'bold',
          size: 12,
        },
        display: function (context) {
          return context.dataset.data[context.dataIndex] > 0
        },
      },
    },
  }

  const getPlayerName = (id?: string) => {
    const member = id && members?.results.find(member => member.originId === id)
    return member ? `${member.firstname} ${member.lastname}` : null
  }

  const getEventOptions = () => {
    const options: DropDownElement[] = []
    events?.results.map((event) => {
      event.id && options.push({
        key: event.id,
        value: event.title,
      })
    })
    return options
  }
  const getMemberOptions = () => {
    const options: DropDownElement[] = []
    statistics?.memberOpponents.map((member) => {
      member.memberId && options.push({
        key: member.memberId,
        value: `${getMember(member.memberId)?.firstname} ${getMember(member.memberId)?.lastname}`,
      })
    })
    return options
  }

  const getMember = (id?: string) => {
    if (id) {
      return members?.results.find(item => item.originId === id)
    }
  }

  const getMemberWinLooseRoundsData = (member: string) => {
    return {
      datasets:
        [
          {
            hoverOffset: 4,
            label: 'Runden',
            data: [statistics?.memberStatistics[member].totalRoundWon || 0,
              statistics?.memberStatistics[member].totalRoundLoose || 0,
            ],
            backgroundColor: [
              theme.palette.primary.light,
              theme.palette.primary.dark,
            ],
          },
        ],
      labels: ['Gewonnen', 'Verloren'],
    }
  }

  const getMemberTableData = (member: string) => {
    return {
      datasets:
        [
          {
            hoverOffset: 4,
            label: 'Anzahl',
            data: [statistics?.memberStatistics[member].tableOfThree || 0,
              statistics?.memberStatistics[member].tableOfFour || 0,
            ],
            backgroundColor: [
              theme.palette.primary.light,
              theme.palette.primary.dark,
            ],
          },
        ],
      labels: ['3er Tische', '4er Tische'],
    }
  }
  const getMemberWinLooseGameData = (member: string) => {
    return {
      datasets:
        [
          {
            hoverOffset: 4,
            label: 'Spiele',
            data: [statistics?.memberStatistics[member].totalGameWon || 0,
              statistics?.memberStatistics[member].totalGameLoose || 0,
              statistics?.memberStatistics[member].totalMixedIn || 0],
            backgroundColor: [
              theme.palette.primary.light,
              theme.palette.primary.dark,
              theme.palette.grey.A400,
            ],
          },
        ],
      labels: ['Gewonnen', 'Verloren', 'Eingemischt'],
    }
  }

  const getCompleteWinLooseGameData = () => {
    return {
      datasets:
        [
          {
            hoverOffset: 4,
            label: 'Spiele',
            data: [statistics?.globalGameStatistics.wonGames - (statistics?.globalGameStatistics.byType['eingemischt']) || 0,
              statistics?.globalGameStatistics.totalGames - statistics?.globalGameStatistics.wonGames || 0,
              statistics?.globalGameStatistics.byType['eingemischt'] || 0],
            backgroundColor: [
              theme.palette.primary.light,
              theme.palette.primary.dark,
              theme.palette.grey.A400,
            ],
          },
        ],
      labels: ['Gewonnen', 'Verloren', 'Eingemischt'],
    }
  }

  const getMemberOpponentData = (member: string) => {
    const labels: string[] = []
    const count: number[] = []
    const opponents = statistics?.memberOpponents.find(element => element.memberId === member)?.opponents
    opponents && Object.keys(opponents).map(opponent => {
      if (member !== opponent) {
        labels.push(`${getMember(opponent)?.firstname} ${getMember(opponent)?.lastname.slice(0, 1)}.`)
        count.push(opponents[opponent])
      }
    })

    const datasets = [
      {
        label: 'Anzahl',
        data: count,
        backgroundColor: theme.palette.primary.dark,
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.8,
      },
    ]
    return {
      labels,
      datasets,
    }
  }
  const getMemberRoundData = (member: string) => {
    const labels: string[] = []
    const first: number[] = []
    const second: number[] = []
    const complete: number[] = []
    statistics?.memberStatistics[member].rounds.map(element => {
      labels.push(element.year.toString())
      first.push(element.first)
      second.push(element.second)
      complete.push(element.first + element.second)
    })

    const datasets = [
      {
        label: 'Erste Runde',
        data: first,
        backgroundColor: theme.palette.primary.dark,
        stack: 'Runden',
        borderRadius: {
          topLeft: 0,
          topRight: 0,
          bottomLeft: 4,
          bottomRight: 4,
        },
        borderSkipped: false,
      },
      {
        label: 'Zweite Runde',
        data: second,
        backgroundColor: theme.palette.primary.light,
        stack: 'Runden',
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0,
        },
        borderSkipped: false,
      },
      {
        label: 'Gesamt',
        data: complete,
        backgroundColor: theme.palette.grey.A400,
        stack: 'Gesamt',
        borderRadius: 4,
        borderSkipped: false,
      },
    ]
    return {
      labels,
      datasets,
    }
  }

  const getParticipationData = () => {
    const labels: string[] = []
    const male: number[] = []
    const female: number[] = []
    statistics?.participation.map(element => {
      labels.push(element.year.toString())
      male.push(element.male)
      female.push(element.female)
    })

    const datasets = [
      {
        label: 'Männer',
        data: male,
        backgroundColor: theme.palette.primary.dark,
        borderRadius: {
          topLeft: 0,
          topRight: 0,
          bottomLeft: 4,
          bottomRight: 4,
        },
        borderSkipped: false,
      },
      {
        label: 'Frauen',
        data: female,
        backgroundColor: theme.palette.primary.light,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0,
        },
        borderSkipped: false,
      },
    ]

    return {
      labels,
      datasets,
    }
  }

  const getGameTypeData = (event?: string) => {
    let globalGameStats = statistics?.globalGameStatistics
    if (event) {
      globalGameStats = statistics?.eventStatistic[event].gameStatistics
    }
    return {
      labels: ['Grand', 'Eichel', 'Grün', 'Rot', 'Schell', 'Null'],
      datasets: [
        {
          data: [
            globalGameStats?.byType.grand || 0,
            globalGameStats?.byType.eichel || 0,
            globalGameStats?.byType.gruen || 0,
            globalGameStats?.byType.rot || 0,
            globalGameStats?.byType.schell || 0,
            globalGameStats?.byType.zero || 0,
          ],
          backgroundColor: [
            '#1c7899', // Grand
            '#a0a8ad', // Eichel
            '#a9c3ce', // Grün
            '#6093a4', // Rot
            '#4e5a60', // Schell
            '#8baab6', // Null
          ],
          label: '',
          barPercentage: 0.7,
          borderRadius: 4,
        },
      ],
    }
  }

  const getGamePropertyData = (event?: string) => {
    let globalGameStats = statistics?.globalGameStatistics
    if (event) {
      globalGameStats = statistics?.eventStatistic[event].gameStatistics
    }
    return {
      labels: [
        'Einfach',
        'Schneider',
        'Schwarz',
        'Hand',
        'Ouvert',
        'Hand-Schneider',
        'Hand-Schwarz',
        'Hand-Schneider-angesagt',
        'Hand-Schwarz-angesagt',
        'Hand-Schneider-angesagt-Schwarz',
        'Hand-Ouvert',
      ],
      datasets: [
        {
          data: [
            globalGameStats?.byProperty['einfach'] || 0,
            globalGameStats?.byProperty['schneider'] || 0,
            globalGameStats?.byProperty['schwarz'] || 0,
            globalGameStats?.byProperty['hand'] || 0,
            globalGameStats?.byProperty['ouvert'] || 0,
            globalGameStats?.byProperty['handSchneider'] || 0,
            globalGameStats?.byProperty['handSchwarz'] || 0,
            globalGameStats?.byProperty['handSchneiderAngesagt'] || 0,
            globalGameStats?.byProperty['handSchwarzAngesagt'] || 0,
            globalGameStats?.byProperty['handSchneiderAngesagtSchwarz'] || 0,
            globalGameStats?.byProperty['handOuvert'] || 0,
          ],
          backgroundColor: [
            statisticColors[0],
            statisticColors[9],
            statisticColors[2],
            statisticColors[8],
            statisticColors[4],
            statisticColors[5],
            statisticColors[6],
            statisticColors[7],
            statisticColors[1],
            statisticColors[3],
            statisticColors[10],
          ],
          borderRadius: 4,
          barPercentage: 0.8,
        },
      ],
    }
  }

  const getMemberGameTypeData = (memberId: string) => {
    const globalGameStats = statistics?.memberStatistics[memberId].gameStatistics
    return {
      labels: ['Grand', 'Eichel', 'Grün', 'Rot', 'Schell', 'Null'],
      datasets: [
        {
          data: [
            globalGameStats?.byType.grand || 0,
            globalGameStats?.byType.eichel || 0,
            globalGameStats?.byType.gruen || 0,
            globalGameStats?.byType.rot || 0,
            globalGameStats?.byType.schell || 0,
            globalGameStats?.byType.zero || 0,
          ],
          backgroundColor: [
            '#1c7899', // Grand
            '#a0a8ad', // Eichel
            '#a9c3ce', // Grün
            '#6093a4', // Rot
            '#4e5a60', // Schell
            '#8baab6', // Null
          ],
          label: '',
          barPercentage: 0.7,
          borderRadius: 4,
        },
      ],
    }
  }

  const getMemberGamePropertyData = (memberId: string) => {
    const globalGameStats = statistics?.memberStatistics[memberId].gameStatistics
    return {
      labels: [
        'Einfach',
        'Schneider',
        'Schwarz',
        'Hand',
        'Ouvert',
        'Hand-Schneider',
        'Hand-Schwarz',
        'Hand-Schneider-angesagt',
        'Hand-Schwarz-angesagt',
        'Hand-Schneider-angesagt-Schwarz',
        'Hand-Ouvert',
      ],
      datasets: [
        {
          data: [
            globalGameStats?.byProperty['einfach'] || 0,
            globalGameStats?.byProperty['schneider'] || 0,
            globalGameStats?.byProperty['schwarz'] || 0,
            globalGameStats?.byProperty['hand'] || 0,
            globalGameStats?.byProperty['ouvert'] || 0,
            globalGameStats?.byProperty['handSchneider'] || 0,
            globalGameStats?.byProperty['handSchwarz'] || 0,
            globalGameStats?.byProperty['handSchneiderAngesagt'] || 0,
            globalGameStats?.byProperty['handSchwarzAngesagt'] || 0,
            globalGameStats?.byProperty['handSchneiderAngesagtSchwarz'] || 0,
            globalGameStats?.byProperty['handOuvert'] || 0,
          ],
          backgroundColor: [
            statisticColors[0],
            statisticColors[9],
            statisticColors[2],
            statisticColors[8],
            statisticColors[4],
            statisticColors[5],
            statisticColors[6],
            statisticColors[7],
            statisticColors[1],
            statisticColors[3],
            statisticColors[10],
          ],
          borderRadius: 4,
          barPercentage: 0.8,
        },
      ],
    }
  }

  enum TabEnum {
    complete = 'complete',
    event = 'event',
    member = 'member',
  }

  const [activeTab, setActiveTab] = React.useState<TabEnum>(TabEnum.complete)
  const [activeGamer, setActiveGamer] = React.useState<string>()
  const [activeEvent, setActiveEvent] = React.useState<string>()

  const [stars, setStars] = React.useState<Stars[]>([])

  const setEventResults = (res: FetchResponse) => {
    dispatch(dataActions.setEventResult({ ...eventResults, [activeEvent]: res.results }))
  }

  useEffect(() => {
    activeEvent && !eventResults[activeEvent] &&
    fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventResults, Method.GET, activeEvent, undefined, setEventResults, null)
  }, [activeEvent])

  useEffect(() => {
    const newStarArray: Stars[] = []
    statistics?.stars && Object.keys(statistics?.stars).map(memberId => {
      newStarArray.push({
        memberId: memberId,
        count: statistics?.stars[memberId],
      })
    })
    setStars(newStarArray.sort((a, b) => b.count - a.count))
  }, [statistics])

  return (
    <Paper sx={{ margin: downSm ? '0px' : '32px', padding: downSm ? '8px' : '32px' }}>
      {statistics
        ? (
          <>
            <Typography variant='h1'>Skatstatistiken</Typography>

            {/* Tabnavigation */}
            <Tabs
              sx={{ marginBottom: '20px' }}
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab key={TabEnum.complete} value={TabEnum.complete} label='Gesamtstatistik' />
              {!downSm && <Tab key={TabEnum.event} value={TabEnum.event} label='Turnierstatistik' />}
              <Tab key={TabEnum.member} value={TabEnum.member} label='Einzelstatistiken' />
            </Tabs>

            {/* TabEnum.event */}
            {activeTab === TabEnum.event &&
              <>
                <Box sx={{ width: '400px', marginBottom: '20px' }}>
                  <DropDownComponent
                    size='small'
                    options={getEventOptions()}
                    label='Skatturnier'
                    multiple={false}
                    onChange={(value) => {
                      setActiveEvent(value?.key)
                    }}
                  />
                </Box>
                {(activeEvent && eventResults[activeEvent])
                  ? (
                    <Grid container spacing={4}>
                      <Grid item xs={6}>
                        <Typography variant='subtitle1'>Platzierung</Typography>
                        <Table size='small' sx={{ width: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell align='center'>
                                <b>Platzierung</b>
                              </TableCell>
                              <TableCell>
                                <b>Name</b>
                              </TableCell>
                              <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                                <b>Runde 1</b>
                              </TableCell>
                              <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                                <b>Runde 2</b>
                              </TableCell>
                              <TableCell align='center'>
                                <b>Gesamtpunkte</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getSortedResults(eventResults[activeEvent], SortingOption.complete).map((element, index) =>
                              <TableRow key={index}>
                                <TableCell align='center'>
                                  <b>{index + 1}</b>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {getPlayerName(element.memberId)}
                                </TableCell>
                                <TableCell align='center'>
                                  {eventResults[activeEvent].find(result => result.session === 1 && result.players.find(player => player === element.memberId))?.results[element.memberId].completeResult}
                                </TableCell>
                                <TableCell align='center'>
                                  {eventResults[activeEvent].find(result => result.session === 2 && result.players.find(player => player === element.memberId))?.results[element.memberId].completeResult}
                                </TableCell>
                                <TableCell align='center'>
                                  <b>{element.completeResult}</b>
                                </TableCell>
                              </TableRow>,
                            )}
                          </TableBody>
                        </Table>
                      </Grid>
                      <Grid item xs={6}>
                        {statistics?.eventStatistic[activeEvent] ? (
                          <>
                            <Typography variant='subtitle1'>Rekorde</Typography>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <FemaleIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Spielerinnen:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {statistics?.eventStatistic[activeEvent].women}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <MaleIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Spieler:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {statistics?.eventStatistic[activeEvent].men}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <CardIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Spiele gesamt:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {statistics?.eventStatistic[activeEvent].totalGames}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <AverageIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Durchschnittl. Spiele pro Spieler:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {statistics?.eventStatistic[activeEvent].averageGamesPerPlayer}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <GoodIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Bestes Spiel:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].bestGame && getMember(statistics?.eventStatistic[activeEvent].bestGame.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].bestGame && getMember(statistics?.eventStatistic[activeEvent].bestGame.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].bestGame && statistics?.eventStatistic[activeEvent].bestGame.count} Punkten`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <BadIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Schlechtestes Spiel:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].worstGame && getMember(statistics?.eventStatistic[activeEvent].worstGame.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].worstGame && getMember(statistics?.eventStatistic[activeEvent].worstGame.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].worstGame && statistics?.eventStatistic[activeEvent].worstGame.count} Punkten`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <OldestGamerIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Ältester Spieler:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].oldestGamer && getMember(statistics?.eventStatistic[activeEvent].oldestGamer.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].oldestGamer && getMember(statistics?.eventStatistic[activeEvent].oldestGamer.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].oldestGamer && statistics?.eventStatistic[activeEvent].oldestGamer.count} Jahren`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <YoungestGamerIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Jüngster Spieler:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].youngestGamer && getMember(statistics?.eventStatistic[activeEvent].youngestGamer.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].youngestGamer && getMember(statistics?.eventStatistic[activeEvent].youngestGamer.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].youngestGamer && statistics?.eventStatistic[activeEvent].youngestGamer.count} Jahren`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <BetterSecondSessionIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Größte Aufholjagd:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].biggestSessionImprovement && getMember(statistics?.eventStatistic[activeEvent].biggestSessionImprovement.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].youngestGamer && getMember(statistics?.eventStatistic[activeEvent].biggestSessionImprovement.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].biggestSessionImprovement && statistics?.eventStatistic[activeEvent].biggestSessionImprovement.count} Punkten`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <BetterFirstSessionIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Größter Abfall nach Runde 1:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].worstSessionDrop && getMember(statistics?.eventStatistic[activeEvent].worstSessionDrop.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].worstSessionDrop && getMember(statistics?.eventStatistic[activeEvent].worstSessionDrop.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].worstSessionDrop && statistics?.eventStatistic[activeEvent].worstSessionDrop.count} Punkten`}
                              </Typography>
                            </Box>
                            <Box
                              sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                            >
                              <ScaleIcon width='20px' />
                              <Typography
                                sx={{ marginLeft: '10px' }}
                                variant='body1'
                              ><b>Geringste Abweichung in Runden:&nbsp;</b>
                              </Typography>
                              <Typography
                                variant='body1'
                              >
                                {`${statistics?.eventStatistic[activeEvent].mostConsistentPlayer && getMember(statistics?.eventStatistic[activeEvent].mostConsistentPlayer.memberId)?.firstname} ${statistics?.eventStatistic[activeEvent].mostConsistentPlayer && getMember(statistics?.eventStatistic[activeEvent].mostConsistentPlayer.memberId)?.lastname} mit ${statistics?.eventStatistic[activeEvent].mostConsistentPlayer && statistics?.eventStatistic[activeEvent].mostConsistentPlayer.count} Punkten`}
                              </Typography>
                            </Box>
                          </>)
                          : (
                            <>
                              <Typography variant='body1'>Für dieses Turnier liegen noch keine Statistiken
                                vor.
                              </Typography>
                            </>
                          )}
                      </Grid>
                      {!downMd && events?.results.find(elem => elem.id === activeEvent).eventTo.year > 2023 &&
                        <>
                          <Grid item xs={12}>
                            <Typography variant='subtitle1'>Spiele</Typography>
                            <Grid item xs={12}>
                              <BarChart
                                chartData={getGameTypeData(activeEvent)}
                                height={100}
                                chartOptions={nonStackedOptions}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <BarChart
                                chartData={getGamePropertyData(activeEvent)}
                                height={100}
                                chartOptions={nonStackedOptions}
                              />
                            </Grid>
                          </Grid>
                        </>}
                    </Grid>
                  )
                  : (
                    <>
                      {activeEvent
                        ? (
                          <Typography variant='body1'>Für dieses Skatturnier liegen noch keine Ergebnisse
                            vor.
                          </Typography>
                        )
                        : (
                          <Typography variant='body1'>Bitte wähle zunächst ein Skatturnier aus.</Typography>
                        )}
                    </>)}
              </>}

            {/* TabEnum.member */}
            {activeTab === TabEnum.member &&
              <>
                <Box sx={{ width: '300px', marginBottom: '20px' }}>
                  <DropDownComponent
                    size='small'
                    options={getMemberOptions()}
                    label='Spieler'
                    multiple={false}
                    onChange={(value) => {
                      setActiveGamer(value?.key)
                    }}
                  />
                </Box>
                {activeGamer && statistics?.memberStatistics[activeGamer]
                  ? (
                    <Grid container spacing={4}>
                      <Grid xs={downMd ? 12 : 4} item>
                        <Typography variant='subtitle1'>Rekorde</Typography>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <CardIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          ><b>Spiele Gesamt:&nbsp;</b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].totalGameWon + statistics?.memberStatistics[activeGamer].totalGameLoose + statistics?.memberStatistics[activeGamer].totalMixedIn} Spiele`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <GoodIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          >
                            <b>
                              Bestes Spiel:&nbsp;
                            </b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].bestGame} Punkte`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <BadIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          >
                            <b>
                              Schlechtestes Spiel:&nbsp;
                            </b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].worstGame} Punkte`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <RoundIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          ><b>Runden Gesamt:&nbsp;</b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].totalRoundWon + statistics?.memberStatistics[activeGamer].totalRoundLoose} Runden`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <BestTournamentIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          ><b>Bestes Turnier:&nbsp;</b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].bestTournament} Punkte`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <WorstTournamentIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          ><b>Schechtestes Turnier:&nbsp;</b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].worstTournament} Punkte`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                          <StarsIcon width='20px' />
                          <Typography
                            sx={{ marginLeft: '10px' }}
                            variant='body1'
                          ><b>Turniersiege:&nbsp;</b>
                          </Typography>
                          <Typography
                            variant='body1'
                          >
                            {`${statistics?.memberStatistics[activeGamer].tournamentsWon} Siege`}
                          </Typography>
                        </Box>
                      </Grid>
                      {!downMd &&
                        <Grid item xs={downMd ? 12 : 8}>
                          <Typography variant='subtitle1'>Turnierrunden</Typography>
                          <BarChart
                            chartData={getMemberRoundData(activeGamer)}
                            height={100}
                            chartOptions={stackedOptions}
                          />
                        </Grid>}
                      <Grid
                        item xs={downMd ? 12 : 4}
                        sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography
                          variant='subtitle1'
                        >Gewonnene&nbsp;/&nbsp;verlorene&nbsp;Spiele
                        </Typography>
                        <Box sx={{ maxWidth: '300px' }}>
                          <PieChart
                            chartOptions={pieOptions}
                            chartData={getMemberWinLooseGameData(activeGamer)}
                            size={400}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item xs={downMd ? 12 : 4}
                        sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography variant='subtitle1'>3er / 4er Tische</Typography>
                        <Box sx={{ maxWidth: '300px' }}>
                          <PieChart
                            chartOptions={pieOptions}
                            chartData={getMemberTableData(activeGamer)}
                            size={400}
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item xs={downMd ? 12 : 4}
                        sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography variant='subtitle1'>Gewonnene / verlorene Runden</Typography>
                        <Box sx={{ maxWidth: '300px' }}>
                          <PieChart
                            chartOptions={pieOptions}
                            chartData={getMemberWinLooseRoundsData(activeGamer)}
                            size={400}
                          />
                        </Box>
                      </Grid>
                      {!downMd && (
                        <Grid item xs={12}>
                          <Typography variant='subtitle1'>Gegner</Typography>
                          <BarChart
                            chartData={getMemberOpponentData(activeGamer)}
                            height={100}
                            chartOptions={stackedOptions}
                          />
                        </Grid>)}
                      {!downMd &&
                        <>
                          <Grid item xs={12}>
                            <Typography variant='body1'>
                              Die folgenden Spielstatistiken beziehen sich ausschließlich auf Spiele ab dem Jahr 2024,
                              da
                              erst
                              seit diesem Zeitpunkt spezifische Spieldaten erfasst wurden.
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant='subtitle1'>Spiele</Typography>
                            <Grid item xs={12}>
                              <BarChart
                                chartData={getMemberGameTypeData(activeGamer)}
                                height={100}
                                chartOptions={nonStackedOptions}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <BarChart
                                chartData={getMemberGamePropertyData(activeGamer)}
                                height={100}
                                chartOptions={nonStackedOptions}
                              />
                            </Grid>
                          </Grid>
                        </>}
                    </Grid>
                  )
                  : (
                    <>
                      {activeGamer
                        ? (
                          <Typography variant='body1'>Für diesen Spieler liegen noch keine Statistiken
                            vor.
                          </Typography>
                        )
                        : <Typography variant='body1'>Bitte wähle zunächst einen Spieler aus.</Typography>}
                    </>
                  )}
              </>}

            {/* TabEnum.complete */}
            {activeTab === TabEnum.complete &&
              <Grid container spacing={4}>
                <Grid item xs={downMd ? 12 : 6}>
                  <Typography variant='subtitle1'>Ewige Skatliste</Typography>
                  <Table size='small' sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell align='center'>
                          <b>Nr.</b>
                        </TableCell>
                        <TableCell>
                          <b>Name</b>
                        </TableCell>
                        {!downSm &&
                          <TableCell align='center'>
                            <b>Teilnahmen</b>
                          </TableCell>}
                        <TableCell align='center'>
                          <b>Gesamtpunkte</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statistics?.eternalList.map((element, index) =>
                        <TableRow key={index}>
                          <TableCell align='center'>
                            <b>{index + 1}</b>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {getPlayerName(element.memberId)}
                          </TableCell>
                          {!downSm &&
                            <TableCell align='center'>
                              {element.participations}
                            </TableCell>}
                          <TableCell align='center'>
                            <b>{element.points}</b>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={downMd ? 12 : 6} container>
                  <Grid item xs={12}>
                    <Typography variant='subtitle1'>Rekorde</Typography>
                    <Table size='small' width='100%'>
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <GoodIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Bestes Spiel:
                            </b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.bestGame.memberId)?.firstname} ${getMember(statistics?.records.bestGame.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.bestGame.count} P.` : `mit ${statistics?.records.bestGame.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BadIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Schlechtestes Spiel:
                            </b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.worstGame.memberId)?.firstname} ${getMember(statistics?.records.worstGame.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.worstGame.count} P.` : `mit ${statistics?.records.worstGame.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <OldestGamerIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Ältester Spieler:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.oldestGamer.memberId)?.firstname} ${getMember(statistics?.records.oldestGamer.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.oldestGamer.count} J.` : `mit ${statistics?.records.oldestGamer.count} Jahren`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <YoungestGamerIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Jüngster Spieler:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.youngestGamer.memberId)?.firstname} ${getMember(statistics?.records.youngestGamer.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.youngestGamer.count} J.` : `mit ${statistics?.records.youngestGamer.count} Jahren`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BestTournamentIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Bestes Turnier:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.bestTournament.memberId)?.firstname} ${getMember(statistics?.records.bestTournament.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.bestTournament.count} P.` : `mit ${statistics?.records.bestTournament.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <WorstTournamentIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Schechtestes Turnier:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.worstTournament.memberId)?.firstname} ${getMember(statistics?.records.worstTournament.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.worstTournament.count} P.` : `mit ${statistics?.records.worstTournament.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <OldestWinnerIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Ältester Gewinner:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.oldestWinner.memberId)?.firstname} ${getMember(statistics?.records.oldestWinner.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.oldestWinner.count} J.` : `mit ${statistics?.records.oldestWinner.count} Jahren`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <YoungestWinnerIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Jüngster Gewinner:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.youngestWinner.memberId)?.firstname} ${getMember(statistics?.records.youngestWinner.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.youngestWinner.count} J.` : `mit ${statistics?.records.youngestWinner.count} Jahren`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BestNewcomerIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Bester Neueinstieg:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.bestNewcomer.memberId)?.firstname} ${getMember(statistics?.records.bestNewcomer.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.bestNewcomer.count} P.` : `mit ${statistics?.records.bestNewcomer.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <StarsIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Meisten Turniersiege:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostTournamentsWon.memberId)?.firstname} ${getMember(statistics?.records.mostTournamentsWon.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostTournamentsWon.count} S.` : `mit ${statistics?.records.mostTournamentsWon.count} Siegen`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <LocalFireDepartmentIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Längste Siegesserie:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.highestWinStreak.memberId)?.firstname} ${getMember(statistics?.records.highestWinStreak.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.highestWinStreak.count} S.` : `mit ${statistics?.records.highestWinStreak.count} Spielen in Folge`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BadSmileyIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Längste Verlustserie:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.longestLosingStreak.memberId)?.firstname} ${getMember(statistics?.records.longestLosingStreak.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.longestLosingStreak.count} S.` : `mit ${statistics?.records.longestLosingStreak.count} verlorenen Spielen in Folge`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <HandIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Meiste gew. Handspiele:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostHandGamesWon.memberId)?.firstname} ${getMember(statistics?.records.mostHandGamesWon.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostHandGamesWon.count} S.` : `mit ${statistics?.records.mostHandGamesWon.count} gewonnenen Handspielen`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <CardsIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Meiste gew. Grands:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostGrandGamesWon.memberId)?.firstname} ${getMember(statistics?.records.mostGrandGamesWon.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostGrandGamesWon.count} S.` : `mit ${statistics?.records.mostGrandGamesWon.count} gewonnenen Grands`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BestRoundResultIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Höchste Rundenpunktzahl:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.highestSingleSession.memberId)?.firstname} ${getMember(statistics?.records.highestSingleSession.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.highestSingleSession.count} P.` : `mit ${statistics?.records.highestSingleSession.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <MostFollowingWinsIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Meiste Turniersiege in Folge:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostConsecutiveTournamentWins.memberId)?.firstname} ${getMember(statistics?.records.mostConsecutiveTournamentWins.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostConsecutiveTournamentWins.count} S.` : `mit ${statistics?.records.mostConsecutiveTournamentWins.count} Siegen in Folge`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <SecondPlaceIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Häufigster zweiter Platz:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostSecondPlaces.memberId)?.firstname} ${getMember(statistics?.records.mostSecondPlaces.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostSecondPlaces.count}` : `mit ${statistics?.records.mostSecondPlaces.count} Zweit-Platzierungen`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <PodiumIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Am häufigsten in den Top 3:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostTopThree.memberId)?.firstname} ${getMember(statistics?.records.mostTopThree.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostTopThree.count}` : `mit ${statistics?.records.mostTopThree.count} Top-3-Platzierungen`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BetterSecondSessionIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Größte Aufholjagd:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.biggestComeback.memberId)?.firstname} ${getMember(statistics?.records.biggestComeback.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.biggestComeback.count} P.` : `mit ${statistics?.records.biggestComeback.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <RocketIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Größter Vorsprung nach Runde 1:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.biggestLead.memberId)?.firstname} ${getMember(statistics?.records.biggestLead.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.biggestLead.count} P.` : `mit ${statistics?.records.biggestLead.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <PerfectIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Bestes perfektes Turnier:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.perfectTournament.memberId)?.firstname} ${getMember(statistics?.records.perfectTournament.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.perfectTournament.count} P.` : `mit ${statistics?.records.perfectTournament.count} Punkten`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <BetterTournamentIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Größte Verbesserung zw. 2 Turnieren:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostImprovedPlayer.memberId)?.firstname} ${getMember(statistics?.records.mostImprovedPlayer.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostImprovedPlayer.count} P.` : `mit ${statistics?.records.mostImprovedPlayer.count} Punkten Unterschied`}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.cell}>
                            <ScaleIcon width='20px' />
                          </TableCell>
                          <TableCell className={classes.cell}>
                            <b>Geringste Abweichung in den Platzierungen:</b>
                          </TableCell>
                          <TableCell className={classes.cell}>
                            {`${getMember(statistics?.records.mostConsistentPlayer.memberId)?.firstname} ${getMember(statistics?.records.mostConsistentPlayer.memberId)?.lastname.charAt(0).toUpperCase()}. ${downSm ? ` ${statistics?.records.mostConsistentPlayer.count} P.` : `mit ${statistics?.records.mostConsistentPlayer.count} Plätzen Unterschied`}`}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='subtitle1'>Siege</Typography>
                  <Table size='small' width='100%'>
                    <TableBody>
                      {stars && stars.sort((a, b) => b.count - a.count).map((elem, index) => {
                        const member = getMember(elem.memberId)

                        return (
                          <TableRow key={elem.memberId}>
                            <TableCell className={classes.cell}>
                              {`${member?.firstname} ${member?.lastname.charAt(0).toUpperCase()}.`}
                            </TableCell>
                            <TableCell className={classes.cell}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {Array.from({ length: elem.count }, (_, i) => {
                                  // Einheitliche Farbe für bessere Optik bei maximal 5 Sternen
                                  const baseColor = theme.palette.primary.main // Gold für alle Sterne

                                  return (
                                    <Box
                                      key={i}
                                      sx={{
                                        position: 'relative',
                                        marginRight: '6px',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': {
                                          transform: 'rotate(10deg)',
                                        },
                                      }}
                                    >
                                      <StarIcon
                                        sx={{
                                          color: baseColor,
                                          fontSize: '1.4rem',
                                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
                                        }}
                                      />
                                      {/* Glanzeffekt */}
                                      {i === 0 && elem.count >= 3 && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: '15%',
                                            left: '15%',
                                            width: '30%',
                                            height: '30%',
                                            borderRadius: '50%',
                                            background: 'white',
                                            opacity: 0.5,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  )
                                })}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>

                </Grid>
                {
                  !downMd &&
                    <Grid item xs={12}>
                      <Typography variant='subtitle1'>Turnierteilnahmen</Typography>
                      <BarChart
                        chartData={getParticipationData()}
                        height={100}
                        chartOptions={stackedOptions}
                      />
                    </Grid>
                }
                {
                  !downMd &&
                    <Grid item xs={12}>
                      <Typography variant='subtitle1'>Wer gegen wen?</Typography>
                      <Table size='small' sx={{ marginTop: '100px' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            {statistics?.memberOpponents.map((member, index) =>
                              <TableCell
                                key={index} sx={{
                                  position: 'relative',
                                  overflow: 'visible',
                                }}
                              >
                                <Box
                                  sx={{
                                    transform: 'rotate(-90deg)',
                                    position: 'absolute',
                                    bottom: 60,
                                    left: -40,
                                    width: 200,
                                    height: 100,
                                  }}
                                >
                                  {`${getMember(member.memberId)?.firstname} ${getMember(member.memberId)?.lastname}`}
                                </Box>
                              </TableCell>
                          )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {statistics?.memberOpponents.map((member, memberIndex) =>
                            <TableRow
                              key={memberIndex}
                              sx={{
                              // Jede zweite Zeile einfärben
                                '&:nth-of-type(even)': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <TableCell>{`${getMember(member.memberId)?.firstname} ${getMember(member.memberId)?.lastname}`}</TableCell>
                              {statistics?.memberOpponents.map((element, index) =>
                                <TableCell
                                  align='center' key={index} sx={{
                                    position: 'relative',
                                    overflow: 'visible',
                                    bgcolor: 'background.paper',
                                    // Jede zweite Spalte einfärben
                                    '&:nth-of-type(even)': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                  }}
                                >
                                  {index === memberIndex
                                    ? <Typography variant='body1' />
                                    : (
                                      <>
                                        {(() => {
                                          const value = element.opponents[statistics?.memberOpponents[memberIndex].memberId] || 0

                                          // Styling basierend auf Wert
                                          let color = theme.palette.primary.light
                                          let fontWeight = 'normal'
                                          let fontSize = '1em'

                                          // Korrigierte Abstufung der Farben und Styles
                                          if (value === 1) {
                                            color = theme.palette.primary.light
                                            fontWeight = 'normal'
                                          } else if (value === 2) {
                                            color = theme.palette.primary.main
                                            fontWeight = 'medium'
                                          } else if (value === 3) {
                                            color = theme.palette.primary.dark
                                            fontWeight = 'bold'
                                          } else if (value === 4) {
                                            color = theme.palette.primary[700]
                                            fontWeight = 'bold'
                                            fontSize = '1.1em'
                                          } else if (value >= 5) {
                                            color = theme.palette.primary[900]
                                            fontWeight = 'bold'
                                            fontSize = '1.2em'
                                          }

                                          return (
                                            <Typography
                                              sx={{
                                                color: color,
                                                fontWeight: fontWeight,
                                                fontSize: fontSize,
                                                ...(value >= 4 && {
                                                  backgroundColor: `${theme.palette.primary.light}30`,
                                                  borderRadius: '4px',
                                                  padding: '2px 4px',
                                                }),
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                  transform: value >= 3 ? 'scale(1.15)' : 'none',
                                                  cursor: value >= 3 ? 'pointer' : 'default',
                                                },
                                              }}
                                            >
                                              {value}
                                            </Typography>
                                          )
                                        })()}
                                      </>
                                    )}
                                </TableCell>
                            )}
                            </TableRow>
                        )}
                        </TableBody>
                      </Table>
                    </Grid>
                }
                <Grid item xs={12}>
                  <Typography variant='body1'>
                    Die folgenden Spielstatistiken beziehen sich ausschließlich auf Spiele ab dem Jahr 2024, da erst
                    seit diesem Zeitpunkt spezifische Spieldaten erfasst wurden.
                  </Typography>
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <Typography
                    variant='subtitle1'
                  >Gewonnene&nbsp;/&nbsp;verlorene&nbsp;Spiele
                  </Typography>
                  <Box sx={{ maxWidth: '300px' }}>
                    <PieChart
                      chartOptions={pieOptions}
                      chartData={getCompleteWinLooseGameData()}
                      size={400}
                    />
                  </Box>
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <Typography variant='subtitle1'>Spiele gesamt</Typography>
                  <Table size='small' width='100%'>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.cell}>
                          <CardIcon width='20px' />
                        </TableCell>
                        <TableCell className={classes.cell}>
                          <b>Anzahl Spiele gesamt:
                          </b>
                        </TableCell>
                        <TableCell className={classes.cell} align='right'>
                          {statistics.globalGameStatistics.totalGames}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.cell}>
                          <CardIcon width='20px' />
                        </TableCell>
                        <TableCell className={classes.cell}>
                          <b>Anzahl Punkte gesamt:
                          </b>
                        </TableCell>
                        <TableCell className={classes.cell} align='right'>
                          {statistics.globalGameStatistics.totalValue}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.cell}>
                          <CardIcon width='20px' />
                        </TableCell>
                        <TableCell className={classes.cell}>
                          <b>Durchschnittliche Punktzahl:
                          </b>
                        </TableCell>
                        <TableCell className={classes.cell} align='right'>
                          {Math.round(statistics.globalGameStatistics.averageValue)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
                {!downMd &&
                  <Grid item xs={12}>
                    <Typography variant='subtitle1'>Spiele</Typography>
                    <Grid item xs={12}>
                      <BarChart
                        chartData={getGameTypeData()}
                        height={100}
                        chartOptions={nonStackedOptions}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <BarChart
                        chartData={getGamePropertyData()}
                        height={100}
                        chartOptions={nonStackedOptions}
                      />
                    </Grid>
                  </Grid>}
              </Grid>}
          </>)
        : (
          <>
            <Preloader />
          </>)}
    </Paper>
  )
}
export default Statistics
